import {$qs} from '../../../../@shared/ts/dom';
import {FeatureFlag} from '../../../../@type/features';
import {cartCalculationAbortController, requestCartCalculation} from '../../payment/order';
import {Carts} from '../../reducers/cartReducer';
import {Feature, startModalLoading, stopModalLoading} from '../../reducers/environmentReducer';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {store} from '../../store';
import {debounce} from '../../util/debounce';
import {gotoPage} from '../modal';
import {renderLongAddress} from './addressLocale';

const BILLING_ADDRESS_FIELDS = [
	'billing_address_1',
	'billing_address_2',
	'billing_city',
	'billing_postcode',
	'billing_state',
	'billing_country',
];

/**
 * Setup event listeners for the billing form.
 */
function initBillingFormEvents() {
	const $billingForm = $qs<HTMLFormElement>('#pp-billing-form');
	if (!$billingForm) {
		throw new Error('Failed to locate the billing form element using the selector "#pp-billing-form"');
	}

	/**
     * Handle rendering the long address display that is shown on the shipping
     * page
     */
	$billingForm.addEventListener('change', () => {
		renderLongAddress();
	});

	/**
	 * Handle recalculating the checkout whenever specific billing fields are
	 * changed. Callback is debounced to reduce multiple requests.
	 */
	$billingForm.addEventListener('change',
		debounce(async (e: Event) => {
			const $target = e.target as HTMLInputElement | null;
			if (!$target) {
				return;
			}

			if (!BILLING_ADDRESS_FIELDS.includes($target.getAttribute('name') ?? '')) {
				return;
			}

			store.dispatch(startModalLoading());
			await requestCartCalculation();
			store.dispatch(stopModalLoading());
		}, 1000, cartCalculationAbortController));

	/**
	 * Prevent the form from submitting when the enter key is pressed unless the
	 * element is a button or textarea.
	 */
	$billingForm.addEventListener('keydown', e => {
		if (e.key !== 'Enter') {
			return;
		}

		const $target = e.target as HTMLInputElement;
		if ($target.tagName === 'BUTTON'
			|| $target.tagName === 'TEXTAREA'
			|| ($target.tagName === 'INPUT' && $target.type === 'submit')) {
			return;
		}

		e.preventDefault();
	});

	/**
	 * Handle validating the billing fields when the form is submitted. Changes
	 * to the next valid page if there are no errors.
	 */
	$billingForm.addEventListener('submit', async (event: Event) => {
		event.preventDefault();

		if (!await PeachPayOrder.billing.reportValidity()) {
			return;
		}

		store.dispatch(startModalLoading());
		if (!await gotoPage('shipping')) {
			store.dispatch(stopModalLoading());
			return;
		}

		await requestCartCalculation();

		store.dispatch(stopModalLoading());
	});

	/**
	 * Handle showing/hiding the billing address fields based on whether the
	 * "Hide the shipping/billing fields for virtual products" setting is enabled.
	 */
	if (Feature.enabled(FeatureFlag.VIRTUAL_PRODUCT_FIELDS)) {
		let previousIsVirtual = false;
		store.subscribe(() => {
			const isVirtual = !Carts.needsShipping();
			if (previousIsVirtual === isVirtual) {
				return;
			}

			previousIsVirtual = isVirtual;

			const disabledFields = [...BILLING_ADDRESS_FIELDS, 'billing_phone'];

			for (const name of disabledFields) {
				const fieldContainer = $qs<HTMLDivElement>(`#${name}_field`);
				const fieldInput = $qs<HTMLInputElement>(`[name="${name}"]`);

				if (isVirtual) {
					fieldContainer?.classList.add('hide');
					fieldInput?.setAttribute('disabled', 'true');
				} else {
					fieldContainer?.classList.remove('hide');
					fieldInput?.removeAttribute('disabled');
				}
			}
		});
	}
}

export {
	initBillingFormEvents,
};
